import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Eye, EyeOff, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { logAcceso } from './ProtectedSectionGuard';
import PhoneInput from './PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SS_KEY = 'webAuthenticated';
const SS_INV = 'webAuthInvitado';

const WebAuthGuard = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [modoLogin, setModoLogin] = useState('general'); // 'general' | 'individual'
  const [credentials, setCredentials] = useState({ username: '', telefono: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);

  const checkAuthStatus = useCallback(async () => {
    try {
      const isAuth = sessionStorage.getItem(SS_KEY);
      if (isAuth === 'true') {
        setIsAuthenticated(true);
        setPasswordRequired(false);
        setCheckComplete(true);
        return;
      }
      const response = await fetch(`${API_URL}/api/web/auth/status`);
      if (response.ok) {
        const data = await response.json();
        const required = data.password_required === true;
        setPasswordRequired(required);
        setModoLogin(data.modo_login || 'general');
        if (!required) setIsAuthenticated(true);
      } else {
        setPasswordRequired(false);
        setIsAuthenticated(true);
      }
    } catch (err) {
      console.error('Error checking auth status:', err);
      setPasswordRequired(false);
      setIsAuthenticated(true);
    } finally {
      setCheckComplete(true);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const body = modoLogin === 'individual'
        ? { telefono: credentials.telefono, password: credentials.password }
        : { username: credentials.username, password: credentials.password };
      const response = await fetch(`${API_URL}/api/web/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem(SS_KEY, 'true');
        if (data.invitado) sessionStorage.setItem(SS_INV, JSON.stringify(data.invitado));
        await logAcceso('raiz');
        setIsAuthenticated(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Usuario o contraseña incorrectos');
      }
    } catch (err) {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  if (!checkComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FDFCFA' }}>
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  if (!passwordRequired || isAuthenticated) {
    return children;
  }

  const isIndividual = modoLogin === 'individual';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FDFCFA' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="webauth-login">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: '#D4846D20' }}>
              <Lock className="w-10 h-10" style={{ color: '#D4846D' }} />
            </div>
          </div>
          <h1 className="text-2xl font-semibold text-center mb-2" style={{ color: '#5C5C5C', fontFamily: 'Playfair Display' }}>
            Acceso Privado
          </h1>
          <p className="text-gray-500 text-center mb-8">
            Esta invitación requiere {isIndividual ? 'que te identifiques' : 'contraseña'} para acceder.
          </p>
          <form onSubmit={handleLogin} className="space-y-4">
            {isIndividual ? (
              <PhoneInput
                value={credentials.telefono}
                onChange={(v) => setCredentials({ ...credentials, telefono: v })}
                label="Teléfono"
                placeholder="Tu número de teléfono"
                required
                testIdSuffix="webauth"
              />
            ) : (
              <div>
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Introduce el usuario"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="mt-1"
                  required
                  data-testid="webauth-input-usuario"
                />
              </div>
            )}
            <div>
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Introduce la contraseña"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="pr-10"
                  required
                  data-testid="webauth-input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-500 text-sm text-center" data-testid="webauth-error">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full text-white py-6"
              style={{ backgroundColor: '#D4846D' }}
              data-testid="webauth-btn-entrar"
            >
              {loading ? 'Verificando...' : 'Entrar'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WebAuthGuard;
